import {
  type AdminActionsQuery,
  AdminActionsQuery$data,
} from '#app/__generated__/relay/AdminActionsQuery.graphql.js';
import { Link } from '#app/components/Link/index.js';
import { useModal } from '#app/components/Modals/V2/GlobalModalSystem/useModal.js';
import { ContraProMenu } from '#app/features/admin/components/AdminPeople/components/PeopleTable/Cells/ContraProCell/ContraProMenu.js';
import { RatingMenu } from '#app/features/admin/components/AdminPeople/components/PeopleTable/Cells/RatingCell/RatingMenu.js';
import { WaitListMenu } from '#app/features/admin/components/AdminPeople/components/PeopleTable/Cells/WaitListCell/WaitListMenu.js';
import { ImpersonateUser } from '#app/features/admin/components/ImpersonateUser/ImpersonateUser.js';
import { UserDetailsModal } from '#app/features/admin/components/UserDetailsModal/UserDetailsModal.js';
import { useUserPortfolioLink } from '#app/hooks/usePortfolioLink.js';
import { CloseIcon } from '@contra/icons/icons';
import { graphql, useLazyLoadQuery } from '@contra/relay';
import { getUrl } from '@contra/routes';
import { Suspense, useState } from 'react';
import { css, styled } from 'styled-components';

const CloseButton = styled.button`
  position: absolute;
  top: -24px;
  right: 6px;
`;

const Container = styled.div`
  ${({ theme }) => css`
    padding: 16px;
    position: fixed;
    bottom: 88px;
    right: 16px;

    background-color: white;
    border-radius: 8px;

    box-shadow: ${theme.boxShadow.toast};
    z-index: ${theme.zIndices.tooltip};

    > * + * {
      margin-bottom: 12px;
      display: block;

      &:last-child {
        margin-bottom: 0px;
      }
    }
  `}
`;

const Email = styled.div`
  max-width: 180px;
  font-size: 14px;
`;

const MenuPosition = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  cursor: pointer;
`;

const transformPlan = (plan) => ({
  boostedJobs: {
    entitlement: plan?.boostedJobs?.entitlement ?? 0,
    expiresAt: new Date(plan?.boostedJobs?.expiresAt),
  },
  contractFees: {
    entitlement: plan?.contractFees?.entitlement ?? 0,
    expiresAt: new Date(plan?.contractFees?.expiresAt),
  },
});

type AdminActionsProps = {
  readonly disablePositioning?: boolean;
  readonly username: string;
};

const InternalAdminActions = ({ username }: AdminActionsProps) => {
  const { adminUserProfileByUsername } = useLazyLoadQuery<AdminActionsQuery>(
    graphql`
      query AdminActionsQuery($username: String!) {
        adminUserProfileByUsername(username: $username) {
          id
          userGroups
          emailAddress
          rating {
            autoBasicScore
            autoBonusScore
            autoProjectsScore
            autoServicesScore
            excludeFromDiscover
            excludeFromMatching
            flagForImprovement
            internalNotes
          }
          userProfile {
            id
            topIndependentStatus
            hubspotContactUrl
            organizationV2 {
              hubspotCompanyUrl
            }
          }
          clientSubscriptionPlanCustomizations {
            BUILD {
              boostedJobs {
                entitlement
                expiresAt
              }
              contractFees {
                entitlement
                expiresAt
              }
            }
            GROW {
              boostedJobs {
                entitlement
                expiresAt
              }
              contractFees {
                entitlement
                expiresAt
              }
            }
            SCALE {
              boostedJobs {
                entitlement
                expiresAt
              }
              contractFees {
                entitlement
                expiresAt
              }
            }
          }
          waitLists {
            edges {
              node {
                id
                nid
              }
            }
          }
        }
      }
    `,
    {
      username,
    },
  );

  const { getPortfolioUrl } = useUserPortfolioLink();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const transformSubscriptionData = (
    data: NonNullable<
      AdminActionsQuery$data['adminUserProfileByUsername']
    >['clientSubscriptionPlanCustomizations'],
  ) => {
    if (!data) return undefined;

    return {
      BUILD: transformPlan(data.BUILD),
      GROW: transformPlan(data.GROW),
      SCALE: transformPlan(data.SCALE),
    };
  };

  const { openModal: openClientSubscriptionCustomizationModal } = useModal({
    identifier: 'ClientSubscriptionCustomizationModal',
    modalProps: {
      initialData:
        adminUserProfileByUsername?.clientSubscriptionPlanCustomizations
          ? transformSubscriptionData(
              adminUserProfileByUsername.clientSubscriptionPlanCustomizations,
            )
          : undefined,
      userProfileId: adminUserProfileByUsername?.userProfile.id ?? '',
    },
  });

  if (!adminUserProfileByUsername?.id || !adminUserProfileByUsername?.rating) {
    return null;
  }

  const { emailAddress, id, rating, userGroups, userProfile } =
    adminUserProfileByUsername;

  const currentRating = {
    rating: {
      basicScore: rating?.autoBasicScore ?? 0,
      bonusScore: rating?.autoBonusScore ?? 0,
      excludeFromDiscover: rating?.excludeFromDiscover ?? false,
      excludeFromMatching: rating?.excludeFromMatching ?? false,
      flagForImprovement: rating?.flagForImprovement ?? false,
      internalNotes: rating?.internalNotes ?? '',
      projectsScore: rating?.autoProjectsScore ?? 0,
      servicesScore: rating?.autoServicesScore ?? 0,
      topIndependentStatus: userProfile.topIndependentStatus,
    },
  };

  const waitLists = adminUserProfileByUsername.waitLists.edges.map(
    ({ node }) => ({ ...node, nid: node.nid }),
  );
  return (
    <>
      <Email>{emailAddress}</Email>

      <ImpersonateUser userProfileId={id} />

      <Link
        target="_blank"
        to={getPortfolioUrl({ urlParameters: null, username })}
      >
        ✨ Portfolio
      </Link>
      <MenuPosition>
        👨‍⚖️ Rating
        <RatingMenu
          currentRating={currentRating}
          userId={id}
        />
      </MenuPosition>
      <MenuPosition>
        🎨 Contra Pro
        <ContraProMenu
          userGroups={userGroups}
          userId={id}
        />
      </MenuPosition>
      <MenuPosition>
        📝 Waitlists
        <WaitListMenu
          userId={id}
          waitLists={waitLists}
        />
      </MenuPosition>
      <MenuPosition onClick={() => openClientSubscriptionCustomizationModal()}>
        🤑 Client subscriptions
      </MenuPosition>

      {userProfile.hubspotContactUrl ? (
        <a
          href={userProfile.hubspotContactUrl}
          rel="noreferrer"
          target="_blank"
        >
          🛂 Hubspot Contact
        </a>
      ) : null}
      {userProfile.organizationV2?.hubspotCompanyUrl ? (
        <a
          href={userProfile.organizationV2.hubspotCompanyUrl}
          rel="noreferrer"
          target="_blank"
        >
          🏭 Hubspot Company
        </a>
      ) : null}
      <MenuPosition
        onClick={() =>
          window.open(
            getUrl('/@internal/discovery/@username', {
              args: {
                username,
              },
            }),
            '_blank',
          )
        }
      >
        💯 Discovery
      </MenuPosition>
      <MenuPosition onClick={() => setIsModalOpen(true)}>
        👀 More Details
      </MenuPosition>
      <UserDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        username={username}
      />
    </>
  );
};

export const AdminActions = ({ username }: AdminActionsProps) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Container>
      {isOpen ? (
        <>
          <CloseButton
            onClick={() => setIsOpen(false)}
            type="button"
          >
            <CloseIcon />
          </CloseButton>

          <Suspense>
            <InternalAdminActions username={username} />
          </Suspense>
        </>
      ) : (
        <button
          onClick={() => setIsOpen(true)}
          type="button"
        >
          Show
        </button>
      )}
    </Container>
  );
};
